import React, { useEffect, useRef } from "react";
import { getCurrentUser, updateCurrentUserTZ } from "../services/api";
import { toast } from "react-toastify";

/** Détecte le fuseau IANA du device (desktop/mobile/PWA) */
function detectTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Estime la différence horaire (en heures) entre deux TZ */
function hoursOffsetBetween(tzA: string, tzB: string): number {
  try {
    const now = new Date();
    const getMs = (tz: string) =>
      new Date(now.toLocaleString("en-US", { timeZone: tz })).getTime();
    const diffMs = getMs(tzB) - getMs(tzA);
    return Math.round(diffMs / 36e5);
  } catch {
    return 99;
  }
}

/** Hook: propose une bannière si l'écart >= thresholdHours (par ex 2h) */
export function useTimeZoneSyncWithBanner({ thresholdHours = 2 }: { thresholdHours?: number } = {}) {
  const syncingRef = useRef(false);
  const shownRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (syncingRef.current) return;
      const token = localStorage.getItem("jwt");
      if (!token) return;
      syncingRef.current = true;

      try {
        const user = await getCurrentUser();
        const detected = detectTimeZone();
        if (!detected) return;

        if (user.time_zone !== detected) {
          const offset = Math.abs(hoursOffsetBetween(user.time_zone, detected));
          if (offset >= thresholdHours && !shownRef.current) {
            shownRef.current = true;

            const id = toast.info(
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Nouveau fuseau horaire détecté
                </div>
                <div style={{ fontSize: 14, marginBottom: 10 }}>
                  Actuel : <b>{user.time_zone}</b> — Détecté : <b>{detected}</b>
                  <br />
                  Passer sur <b>{detected}</b> ?
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    onClick={() => toast.dismiss(id)}
                    style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6 }}
                  >
                    Garder {user.time_zone}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await updateCurrentUserTZ(detected);
                        toast.dismiss(id);
                        toast.success(`Fuseau mis à jour : ${detected}`);
                      } catch (e: any) {
                        toast.error(e?.message || "Échec de la mise à jour");
                      }
                    }}
                    style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6 }}
                  >
                    Passer sur {detected}
                  </button>
                </div>
              </div>,
              { autoClose: 10000 }
            );

            // sécurité: ferme le toast si onglet inactif trop longtemps
            setTimeout(() => toast.dismiss(id), 10000);
          }
        }
      } catch (e: any) {
        if (e?.response?.status === 401) {
          localStorage.removeItem("jwt");
          localStorage.removeItem("user");
          return;
        }
      } finally {
        syncingRef.current = false;
      }
    };

    // au lancement
    check();

    // au retour en avant-plan (PWA / mobile / desktop)
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [thresholdHours]);
}
